import { Fragment, useCallback, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  usePropertyField,
  useCollectionBinding,
  usePropertyAddressUrl,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import {
  MultihubLogoMonoBlack,
  FacebookIcon,
  InstagramIcon,
  YouTubeIcon,
  TikTokIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Black variant of the Repli wordmark for use on the footer's light
// background. Source SVG mirrors `repli_white.svg` with the wordmark's
// `fill:#fff` swapped to `#1a1a1a`.
import repliBlackLogoUrl from '../assets/repli_black.svg';
import { useCtaColors } from '../hooks/useCtaColors';

// ============================================================================
// Types & helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Strip leading country code "1" if 11 digits
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  // Already has am/pm — just normalize casing
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  // Parse 24h "HH:MM" or "H:MM"
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  label: string;
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the provided entries by their canonical day key so we can look each
  // day up when walking the full week below.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) byDay.set(key, entry);
  }

  const groups: GroupedHours[] = [];

  // Walk every day of the week. Days without provided hours (e.g. Sunday)
  // render as "Closed" rather than being omitted.
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayLabel = DAY_LABELS[key];
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed =
      !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the previous group's range — keep both the full and short label
      // ranges in sync so the renderer can pick whichever fits the layout.
      const dash = prev.label.indexOf(' - ');
      const startLabel = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${startLabel} - ${dayLabel}`;
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ label: dayLabel, shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    // Same community mark as the header. Mono black stays available as a
    // suggestion, but it is not the default — forcing it on the light footer
    // flattened full-color logos.
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_company_mono_black_logo', suggestedFields: ['property_company_mono_black_logo', 'property_company_logo', 'property_company_mono_white_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const poweredByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: { label: 'Address Text', type: 'text', defaultField: 'property_full_address', defaultValue: '123 Property Address, City, ST 00000' },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place/business/address
    // fields). An explicit override from the editor still takes precedence.
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Weidner-specific: "Text Us!" link. URL binds to the per-property
// `custom_text_us_url` custom field — the link is suppressed entirely when no
// URL is set on the property (and no editor override is provided) so
// communities without an SMS landing page don't render a dead link.
const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us!' },
    url: { label: 'URL', type: 'url', defaultField: 'custom_text_us_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Social profile links — mirrors parent Homestead footer (logo row, above the
// divider). Each icon only renders when its URL resolves so communities
// without a given network don't show an empty/dead icon.
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_youtube_url', suggestedFields: ['property_youtube_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_tiktok_url', suggestedFields: ['property_tiktok_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Official Weidner Cares / Love Notes brand marks from the Blue Haven
// company theme. Defaults point at the real program logos (brush-heart
// "weidner cares" wordmark, and "love notes." with a heart-o). The
// renderer knocks uploaded/CDN marks to a mono dark silhouette so they
// stay readable on Homestead's light footer. When no image is set, CSS
// wordmarks below keep a currentColor fallback without a hosted asset.
const weidnerCaresLogoDef: SlotGroupDefinition = {
  label: 'Weidner Cares Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultValue: 'https://media.multihub.io/company/5726763346821120/019d44f3-3452-767e-8966-83b6939f057f.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/weidnercares' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const loveNotesLogoDef: SlotGroupDefinition = {
  label: 'Love Notes Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultValue: 'https://media.multihub.io/company/5726763346821120/019d44f3-3412-75a9-9aac-8f5678cb59df.webp',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/lovenotes' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Mono CSS wordmarks (Weidner Cares / Love Notes)
// ----------------------------------------------------------------------------
// No dedicated mono assets ship with this theme. When a program logo
// image is present we CSS-filter it to a dark silhouette; when cleared,
// these currentColor wordmarks inherit the footer text color.
// ============================================================================

function HeartMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function WeidnerCaresWordmark() {
  return (
    <span className="inline-flex items-center gap-2.5 text-foreground">
      <HeartMark className="h-8 w-8 flex-shrink-0" />
      <span className="flex flex-col leading-none">
        <span className="font-heading text-[13px] font-semibold tracking-[0.2em] uppercase">
          Weidner
        </span>
        <span className="font-body text-[11px] font-normal tracking-[0.32em] uppercase mt-1">
          Cares
        </span>
      </span>
    </span>
  );
}

function LoveNotesWordmark() {
  return (
    <span className="inline-flex items-center gap-1.5 text-foreground">
      <span className="inline-flex items-center font-heading text-[20px] font-semibold tracking-[0.04em] leading-none">
        Lov
        <HeartMark className="h-[0.85em] w-[0.85em] mx-[0.06em] translate-y-[0.04em]" />
      </span>
      <span className="font-body text-[12px] font-normal tracking-[0.28em] uppercase leading-none">
        Notes
      </span>
    </span>
  );
}

function ProgramLogo({
  image,
  alt,
  fallback,
}: {
  image: string | null | undefined;
  alt: string;
  fallback: 'cares' | 'lovenotes';
}) {
  if (hasValue(image)) {
    return (
      <VibesitesImage
        url={image}
        alt={alt}
        className="h-10 w-auto max-w-[140px] object-contain"
        sizes="140px"
        style={{ filter: 'brightness(0)' }}
      />
    );
  }
  return fallback === 'lovenotes' ? <LoveNotesWordmark /> : <WeidnerCaresWordmark />;
}

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  // Global CTA hover CSS + :root contrast vars (idempotent with Header).
  // Footer is always in DefaultLayout, so child sites that override Header
  // still inherit the shared hover treatment.
  useCtaColors();

  const phone = useContentToken('footer.phone');
  const cortButtonText = useContentToken('footer.cort_button_text');
  const cortLeadFormId = (usePropertyField('custom_cort_lead_form_id') as string) || '';

  const openCortModal = useCallback(() => {
    const cort =
      typeof window !== 'undefined'
        ? (window as unknown as { Cort_modal?: { open?: () => void } }).Cort_modal
        : undefined;
    cort?.open?.();
  }, []);

  const logo = useSlotGroup('footer.logo', logoSlotDef);
  // Slot image (primary logo) wins. Mono black is only a stand-in when the
  // property has no primary logo, so the footer doesn't drop to the text
  // placeholder solely because the color mark is missing.
  const monoBlackLogo = usePropertyField('property_mono_black_logo');
  const footerLogoImage = logo.image || monoBlackLogo;
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  // No dedicated management-company logo? Fall back to the property's secondary/
  // company logo — the same image the header drawer shows.
  const secondaryLogo = usePropertyField('property_secondary_logo');
  const managedByImage = managedByLogo.image || secondaryLogo;
  const poweredByLogo = useSlotGroup('footer.powered_by_logo', poweredByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  // Weidner-only: Text Us! link (see textUsLinkDef above).
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — the
  // hook builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  // Weidner-only: suppress the Text Us! link when no `custom_text_us_url` is
  // set on the property (and no editor override has been provided) — Weidner
  // properties without an SMS landing page should not render a dead link.
  const showTextUsLink = hasValue(textUsLink.url);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const weidnerCaresLogo = useSlotGroup('footer.weidner_cares_logo', weidnerCaresLogoDef);
  const loveNotesLogo = useSlotGroup('footer.love_notes_logo', loveNotesLogoDef);
  const facebookLink = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const youtubeLink = useSlotGroup('footer.youtube_link', youtubeLinkDef);
  const tiktokLink = useSlotGroup('footer.tiktok_link', tiktokLinkDef);
  const hasSocialLinks = Boolean(
    facebookLink.url || instagramLink.url || youtubeLink.url || tiktokLink.url,
  );

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  // Split the address on its first comma so the street sits on line 1 and the
  // city/state/zip falls to line 2 (matches the stacked layout in the design).
  const addressText = addressLink.text || '123 Property Address, City, ST 00000';
  const commaIndex = addressText.indexOf(',');
  const addressLine1 = commaIndex !== -1 ? addressText.slice(0, commaIndex) : addressText;
  const addressLine2 = commaIndex !== -1 ? addressText.slice(commaIndex + 1).trim() : '';

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  // The Yardi RentCafe IQ widget is injected at runtime by an external script
  // and then writes its own inline `style="z-index: …"` on the launcher after
  // mount, which beats a stylesheet declaration without `!important`. The CSS
  // rule further down in the render covers the three confirmed launcher nodes
  // as a cheap first pass; this effect is the authoritative fix.
  //
  // Approach: watch document.body for added/changed nodes that match the real
  // launcher (`.rcc-app-cta-wrap`, `.rcc-app-controls`,
  // `#conversations-webchat-toggle`). Pin those nodes at 120 with `!important`
  // so they sit just above the open header (z-50 / drawer z-60) without
  // lifting unrelated vendor iframes or a generic outermost `position: fixed`
  // ancestor. `setProperty` with the `important` priority is the only way to
  // override the inline style the widget sets via its own JS.
  //
  // Scoped to the Footer because Footer is rendered on every page via
  // DefaultLayout, so we get a single global mount point without having to
  // introduce a new always-on component.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el instanceof HTMLElement && el.matches(LAUNCHER_SELECTOR);
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
      // Only lift an outermost fixed ancestor when it is the chat wrap —
      // never pin a generic portal / page-level fixed parent.
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches('.rcc-app-cta-wrap')
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      // The root node itself can also be a match (querySelectorAll skips it).
      if (root instanceof Element) processCandidate(root);
    }

    // Initial pass — catches the widget if it's already mounted by the time
    // the Footer hydrates (e.g. injected from <head> before React boots).
    scanSubtree(document.body);

    // Coalesce bursts of mutations into a single rAF-scheduled scan so we
    // don't thrash on chat widgets that animate (every transform tick fires
    // an attribute mutation).
    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      // Only react to attribute changes that could affect identification or
      // stacking — anything else (e.g. inline transforms during animation) is
      // noise we want to ignore.
      attributeFilter: ['id', 'class', 'style'],
    });

    // ─── RentCafe IQ open→close flash suppress ─────────────────────────────
    // With AUTO_LOAD=true, the vendor mounts the chat panel open, then
    // restore() adds `*-closed` and plays app-out. That flashes a blank
    // iframe on every full page load (e.g. header nav). Re-apply `*-closed`
    // synchronously when the user previously dismissed the panel so the
    // AUTO_LOAD mount never paints the open iframe before restore() runs.
    const CHAT_CONTAINER_SELECTOR =
      '[id^="conversations-"][id$="-container"], .webchat-container, .textus-container';

    function readClosedPreference(closedClass: string): boolean | null {
      try {
        return JSON.parse(window.localStorage.getItem(closedClass) ?? 'null') as boolean | null;
      } catch {
        return null;
      }
    }

    // Leave `stored !== true` alone: `false` means left open; `null` means no
    // preference yet (vendor AUTO_LOAD may intentionally open).
    function suppressChatPanelFlash(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      const idMatch = el.id?.match(/^conversations-(.+)-container$/);
      const widgetType = idMatch?.[1]
        ?? (el.classList.contains('textus-container') ? 'textus' : null)
        ?? (el.classList.contains('webchat-container') ? 'webchat' : null);
      if (!widgetType) return;

      const closedClass = `${widgetType}-closed`;
      if (readClosedPreference(closedClass) !== true) return;
      el.classList.add(closedClass);
    }

    function scanChatContainers(root: ParentNode) {
      if (root instanceof Element) suppressChatPanelFlash(root);
      const containers = root.querySelectorAll?.(CHAT_CONTAINER_SELECTOR);
      if (!containers) return;
      for (const c of Array.from(containers)) suppressChatPanelFlash(c);
    }

    // Initial pass
    scanChatContainers(document.body);

    // Must run synchronously (not rAF-batched) so we add `*-closed`
    // before the browser paints the AUTO_LOAD open state.
    const flashObserver = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of Array.from(r.addedNodes)) {
          if (node instanceof Element) scanChatContainers(node);
        }
      }
    });
    flashObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      flashObserver.disconnect();
      observer.disconnect();
    };
  }, []);

  return (
    <footer
      className="text-[var(--vibe-color-text)]"
      style={{ backgroundColor: 'var(--vibe-color-background)' }}
    >
      {/* ---- Main Footer Content: light surface block ----
          White edges frame the gray block via responsive padding on the
          background-colored wrapper (the footer's white shows through), the
          same boxed treatment the ImageCallouts section uses. */}
      <div className="bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 py-3 sm:py-5 md:py-6">
        {/* Fixed off-white — intentionally not a theme token so brand
            surface swaps don't recolor this footer block. */}
        <div style={{ backgroundColor: '#F5F3EF' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">

          {/* Logo + social, centered above the divider */}
          <div className="flex flex-col items-center gap-6 mb-10">
            <EditableSlotGroup groupPath="footer.logo" definition={logoSlotDef}>
              <a href="/" className="block">
                {footerLogoImage ? (
                  <VibesitesImage
                    url={footerLogoImage}
                    alt={logo.alt || 'Community Logo'}
                    className="h-16 w-auto max-w-[200px] object-contain"
                    sizes="200px"
                  />
                ) : (
                  <div className="font-heading text-2xl text-[var(--vibe-color-text)]">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {hasSocialLinks ? (
              <nav className="flex items-center gap-4" aria-label="Social media">
                {facebookLink.url ? (
                  <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                    <a
                      href={facebookLink.url}
                      target={facebookLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={facebookLink.label || 'Facebook'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <FacebookIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {instagramLink.url ? (
                  <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                    <a
                      href={instagramLink.url}
                      target={instagramLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={instagramLink.label || 'Instagram'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <InstagramIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {youtubeLink.url ? (
                  <EditableSlotGroup groupPath="footer.youtube_link" definition={youtubeLinkDef}>
                    <a
                      href={youtubeLink.url}
                      target={youtubeLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={youtubeLink.label || 'YouTube'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <YouTubeIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
                {tiktokLink.url ? (
                  <EditableSlotGroup groupPath="footer.tiktok_link" definition={tiktokLinkDef}>
                    <a
                      href={tiktokLink.url}
                      target={tiktokLink._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label={tiktokLink.label || 'TikTok'}
                      className="text-[var(--vibe-color-text)] hover:text-[var(--vibe-color-primary)] transition-colors"
                    >
                      <TikTokIcon size={20} />
                    </a>
                  </EditableSlotGroup>
                ) : null}
              </nav>
            ) : null}
          </div>

          {/* Top divider */}
          <hr className="border-t border-[var(--vibe-color-border)]" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mt-10 md:mt-14">

            {/* Contact */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.contact_label"
                defaultValue="<p>Contact</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              {/* Phone + Text Us! — Text Us nests under the phone so both share
                  the contact column. Text Us! (Weidner-only) only renders when
                  `custom_text_us_url` (or an editor URL override) is set;
                  properties without an SMS landing page get just the phone. */}
              <div className="flex flex-col items-center gap-1">
              <a
                href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors"
              >
                <EditableToken
                  path="footer.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
              {showTextUsLink && (
                <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                  <a
                    href={textUsLink.url || '#'}
                    target={textUsLink._target || '_blank'}
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 whitespace-nowrap font-body text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors"
                  >
                    <svg
                      aria-hidden="true"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    {textUsLink.text || 'Text Us!'}
                  </a>
                </EditableSlotGroup>
              )}
              </div>

              {/* Weidner Cares + Love Notes under the contact row. CDN marks are
                  CSS-filtered to a dark silhouette on Homestead's light surface;
                  empty image slots fall back to CSS wordmarks in currentColor. */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 mt-5">
                <EditableSlotGroup groupPath="footer.weidner_cares_logo" definition={weidnerCaresLogoDef}>
                  <a
                    href={weidnerCaresLogo.url || 'https://www.weidner.com/weidnercares'}
                    target={weidnerCaresLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={weidnerCaresLogo.alt || 'Weidner Cares'}
                    className="inline-flex items-center transition-opacity duration-200 hover:opacity-80"
                  >
                    <ProgramLogo
                      image={weidnerCaresLogo.image}
                      alt={weidnerCaresLogo.alt || 'Weidner Cares'}
                      fallback="cares"
                    />
                  </a>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="footer.love_notes_logo" definition={loveNotesLogoDef}>
                  <a
                    href={loveNotesLogo.url || 'https://www.weidner.com/lovenotes'}
                    target={loveNotesLogo._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={loveNotesLogo.alt || 'Love Notes'}
                    className="inline-flex items-center transition-opacity duration-200 hover:opacity-80"
                  >
                    <ProgramLogo
                      image={loveNotesLogo.image}
                      alt={loveNotesLogo.alt || 'Love Notes'}
                      fallback="lovenotes"
                    />
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Location */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.location_label"
                defaultValue="<p>Location</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-text)] transition-colors block leading-relaxed"
                >
                  <span className="block">{addressLine1}</span>
                  {addressLine2 ? <span className="block">{addressLine2}</span> : null}
                </a>
              </EditableSlotGroup>
            </div>

            {/* Hours */}
            <div className="flex flex-col items-center text-center">
              <RichTextContent
                path="footer.hours_label"
                defaultValue="<p>Hours</p>"
                as="h3"
                className="font-heading text-base font-bold text-[var(--vibe-color-text)] mb-4"
              />
              {groupedHours.length > 0 ? (
                // Single grid so the day and time columns line up across every
                // row; `self-center` centers the aligned block under the "Hours" label.
                <dl className="self-center grid grid-cols-[auto_auto] gap-x-6 gap-y-2 text-left font-body text-sm text-[var(--vibe-color-text-muted)]">
                  {groupedHours.map((group, index) => {
                    // Abbreviate multi-day ranges ("Mon - Fri"); keep single
                    // days spelled out ("Saturday") to match the design.
                    const dayDisplay = group.label.includes(' - ') ? group.shortLabel : group.label;
                    return (
                      <Fragment key={index}>
                        <dt className="text-left">{dayDisplay}</dt>
                        <dd className="text-left">{group.hours}</dd>
                      </Fragment>
                    );
                  })}
                </dl>
              ) : (
                <p className="font-body text-sm text-[var(--vibe-color-text-muted)]/60">Office hours will be displayed here.</p>
              )}

              {/* CORT lead form CTA — hidden unless the property has a CORT form ID */}
              {cortLeadFormId ? (
                <div className="mt-4 flex w-full justify-center">
                  <button
                    type="button"
                    onClick={openCortModal}
                    className="footer-cort-cta inline-flex w-full max-w-xs items-center justify-center rounded-sm bg-secondary px-7 py-3.5 font-body text-sm font-semibold tracking-[0.03em] text-secondary-foreground lg:w-auto lg:max-w-none lg:px-[1.75rem] lg:py-[0.95rem]"
                  >
                    <EditableToken
                      path="footer.cort_button_text"
                      as="span"
                      className="text-center text-balance leading-snug lg:whitespace-nowrap"
                    >
                      {cortButtonText || 'Furnish Your Home with CORT'}
                    </EditableToken>
                  </button>
                </div>
              ) : null}
            </div>

          </div>
          </div>
        </div>
      </div>

      {/* ---- Compliance Icons + Legal Nav ---- */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-3">
        <div className="flex flex-col items-center gap-4">
          {/* Compliance icons in a row */}
          <div className="flex items-center justify-center gap-8 text-[var(--vibe-color-text)]">
            <FairHousingIcon size={36} color="currentColor" />
            <PetsAllowedIcon size={32} color="currentColor" />
            <WheelchairIcon size={32} color="currentColor" />
          </div>

          {/* Legal nav links with dot separator */}
          <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-[13px] text-[var(--vibe-color-text-muted)]" aria-label="Legal links">
            <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
              <a href={accessibilityLink.url || '/accessibility'} target={accessibilityLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {accessibilityLink.text || 'Accessibility'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
              <a href={privacyPolicyLink.url || '/privacy-policy'} target={privacyPolicyLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {privacyPolicyLink.text || 'Privacy Policy'}
              </a>
            </EditableSlotGroup>
            <span aria-hidden="true" className="text-[var(--vibe-color-text-muted)]/50">·</span>
            <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
              <a href={sitemapLink.url || '/sitemap'} target={sitemapLink._target || '_self'} className="hover:text-[var(--vibe-color-text)] transition-colors">
                {sitemapLink.text || 'Sitemap'}
              </a>
            </EditableSlotGroup>
          </nav>
        </div>
      </div>

      {/* ---- Bottom Credit Bar: Managed By / Powered By / Design By / Copyright ---- */}
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-0 pb-6">
          <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-5 font-body text-[10px] text-[var(--vibe-color-text-muted)]">

            {/* Managed By */}
            <EditableSlotGroup
              groupPath="footer.managed_by_logo"
              definition={managedByLogoDef}
              className="flex justify-center"
            >
              {managedByImage ? (
                <a
                  href={managedByLogo.url || '#'}
                  target={managedByLogo._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
                >
                  <RichTextContent
                    path="footer.managed_by_text"
                    defaultValue="<p>Professionally Managed by</p>"
                    as="span"
                    className="text-[var(--vibe-color-text-muted)]"
                  />
                  <VibesitesImage
                    url={managedByImage}
                    alt={managedByLogo.alt || 'Property Management Company'}
                    className="h-4 w-auto max-w-[110px] object-contain"
                    sizes="110px"
                  />
                </a>
              ) : null}
            </EditableSlotGroup>

            {/* Powered By (Multihub) */}
            <EditableSlotGroup groupPath="footer.powered_by_logo" definition={poweredByLogoDef}>
              <a
                href={poweredByLogo.url || 'https://www.multihub.io/'}
                target={poweredByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.powered_by_text"
                  defaultValue="<p>Powered by</p>"
                  as="span"
                  className="text-[var(--vibe-color-text-muted)]"
                />
                <MultihubLogoMonoBlack height={14} alt={poweredByLogo.alt || 'Multihub'} />
              </a>
            </EditableSlotGroup>

            {/* Website Design By (Repli) */}
            <EditableSlotGroup groupPath="footer.design_by_logo" definition={designByLogoDef}>
              <a
                href={designByLogo.url || 'https://www.repli360.com/'}
                target={designByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="flex items-center gap-2 opacity-90 hover:opacity-100 transition-opacity"
              >
                <RichTextContent
                  path="footer.design_by_text"
                  defaultValue="<p>Website Design by</p>"
                  as="span"
                  className="text-[var(--vibe-color-text-muted)]"
                />
                <VibesitesImage
                  url={repliBlackLogoUrl}
                  alt={designByLogo.alt || 'Repli Logo'}
                  height={16}
                  className="h-4 w-auto"
                  sizes="80px"
                  priority
                />
              </a>
            </EditableSlotGroup>

            {/* Copyright — year auto-updates from the runtime clock so the
                footer never goes stale. Full-width centered row on mobile;
                inline on desktop. */}
            <span className="w-full text-center md:w-auto">
              © {new Date().getFullYear()} All Rights Reserved.
            </span>

          </div>
        </div>
      </div>

      {/* ---- Global z-index fallback for the RentCafe IQ chatbot ----
          Cheap first pass for the confirmed launcher nodes. The
          authoritative fix lives in the `useEffect` above this `return` —
          it uses a MutationObserver and inline `!important` to beat the
          z-index the widget writes via its own JS after mount. */}
      <style>{`
        /* Production RentCafe Connect / IQ launcher only: the floating wrap
           is .rcc-app-cta-wrap, the controls layer is .rcc-app-controls,
           the bubble is #conversations-webchat-toggle. 120 sits just above
           the open header; !important keeps the vendor inline style from
           winning. */
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }

        /* Neutralize RentCafe open/close slide so restore()-to-closed does
           not animate a blank chat iframe into view on every page load. */
        #conversations-webchat-container,
        #conversations-textus-container,
        .webchat-container,
        .textus-container {
          animation: none !important;
        }
        #conversations-webchat-container:not(.webchat-closed),
        #conversations-textus-container:not(.textus-closed),
        .webchat-container:not(.webchat-closed),
        .textus-container:not(.textus-closed) {
          transform: translateY(0);
          opacity: 1;
          visibility: visible;
        }
        #conversations-webchat-container.webchat-closed,
        #conversations-textus-container.textus-closed,
        .webchat-container.webchat-closed,
        .textus-container.textus-closed {
          transform: translateY(100vh) !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>

    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact</p>',
    },
    'footer.location_label': {
      label: 'Location Label',
      type: 'richtext',
      defaultValue: '<p>Location</p>',
    },
    'footer.hours_label': {
      label: 'Hours Label',
      type: 'richtext',
      defaultValue: '<p>Hours</p>',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.powered_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.powered_by_logo': poweredByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.weidner_cares_logo': weidnerCaresLogoDef,
    'footer.love_notes_logo': loveNotesLogoDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.youtube_link': youtubeLinkDef,
    'footer.tiktok_link': tiktokLinkDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as any).contentSchema = contentSchema;

export default Footer;
